import { useEffect, useRef, useState } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { io } from 'socket.io-client';
import { SOCKET_URL } from '../../lib/utils';
import { useAppSelector } from '../../store';
import { canAccessCommand } from '../../lib/permissions';
import { useActiveEvent } from '../../contexts/EventContext';

interface ChatMsg {
  id: string;
  userId?: string;
  channelType: string;
  targetUserId?: string;
  groupId?: string;
  from: string;
  fromName: string;
  text: string;
  eventId?: string;
}

interface ChatAlertItem {
  id: string;
  fromName: string;
  text: string;
  channelLabel: string;
}

interface CommandChatAlertProps {
  chatOpen: boolean;
  onOpenChat: () => void;
}

function playMessageChime() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start();
    osc.stop(ctx.currentTime + 0.3);
    setTimeout(() => ctx.close(), 500);
  } catch {
    /* ignore */
  }
}

function messageIsForUser(msg: ChatMsg, userId: string, eventId: string | null): boolean {
  const senderId = msg.userId || msg.from;
  if (!senderId || senderId === userId) return false;
  if (msg.channelType === 'command') {
    return !eventId || !msg.eventId || msg.eventId === eventId;
  }
  if (msg.channelType === 'private') {
    return msg.targetUserId === userId;
  }
  if (msg.channelType === 'group') {
    return true;
  }
  return false;
}

function getChannelLabel(msg: ChatMsg): string {
  if (msg.channelType === 'private') return 'Direct message';
  if (msg.channelType === 'group') return 'Group message';
  return 'Command channel';
}

export default function CommandChatAlert({ chatOpen, onOpenChat }: CommandChatAlertProps) {
  const { token, user } = useAppSelector((state) => state.auth);
  const { activeEventId } = useActiveEvent();
  const [alert, setAlert] = useState<ChatAlertItem | null>(null);
  const [queueCount, setQueueCount] = useState(0);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const chatOpenRef = useRef(chatOpen);

  useEffect(() => {
    chatOpenRef.current = chatOpen;
  }, [chatOpen]);

  useEffect(() => {
    if (!canAccessCommand(user?.role)) return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, [user?.role]);

  useEffect(() => {
    if (!token || !user || !canAccessCommand(user.role)) return;

    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      auth: { token },
    });

    socket.on('chat:new_message', (msg: ChatMsg) => {
      if (!messageIsForUser(msg, user.id, activeEventId)) return;
      if (seenIdsRef.current.has(msg.id)) return;
      seenIdsRef.current.add(msg.id);

      if (chatOpenRef.current) return;

      const item: ChatAlertItem = {
        id: msg.id,
        fromName: msg.fromName,
        text: msg.text,
        channelLabel: getChannelLabel(msg),
      };
      setAlert(item);
      setQueueCount((c) => c + 1);
      playMessageChime();

      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          new Notification(`New message — ${msg.fromName}`, {
            body: msg.text,
            tag: `chat-${msg.id}`,
            icon: '/pwa-192x192.png',
          });
        } catch {
          /* ignore */
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [token, user, activeEventId]);

  useEffect(() => {
    if (chatOpen) {
      setAlert(null);
      setQueueCount(0);
    }
  }, [chatOpen]);

  if (!alert || chatOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[99990] w-[min(100vw-2rem,360px)] animate-in slide-in-from-right-4 duration-200">
      <div className="rounded-xl border border-primary/30 bg-card/95 backdrop-blur-md shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-primary/10">
          <div className="flex items-center gap-2 text-primary">
            <MessageSquare className="w-4 h-4" />
            <span className="text-xs font-bold">New message</span>
            {queueCount > 1 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground font-bold">
                +{queueCount - 1}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setAlert(null)}
            className="p-1 rounded hover:bg-accent transition-colors"
            title="Dismiss"
          >
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>
        <button
          type="button"
          onClick={onOpenChat}
          className="w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors"
        >
          <p className="text-xs font-semibold text-foreground">{alert.fromName}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{alert.channelLabel}</p>
          <p className="text-sm text-foreground/90 mt-2 line-clamp-2">{alert.text}</p>
          <p className="text-[10px] text-primary font-semibold mt-2">Open messages</p>
        </button>
      </div>
    </div>
  );
}
